import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PDFDownloadLink, pdf } from '@react-pdf/renderer';
import { Search, Pencil, Send, Check, XCircle, Trash2, FileText, Upload, Mail, X } from 'lucide-react';
import PreventivoPDF from '../components/PreventivoPDF';
import { preventivi as preventiviApi, clienti as clientiApi, cantieri as cantieriApi, documenti as documentiApi } from '../utils/api';
import { InvioClienteModal, InvioClientiModal } from '../components/invio/InvioDocumentoModals';
import useIsMobile from '../hooks/useIsMobile';
import LimiteCantieriModal from '../components/cantieri/LimiteCantieriModal';
import { limiteCantieriDaErrore } from '../components/cantieri/limiteCantieri';
import NomeGiaPresenteModal from '../components/shared/NomeGiaPresenteModal';
import { nomeGiaPresenteDaErrore, stessoNome } from '../components/shared/nomeGiaPresente';
import { useListMode, ListModeToggle } from '../components/shared/ListView';
import DataCard from '../components/shared/DataCard';
import RankingTable from '../components/shared/RankingTable';
import ServizioAutocomplete from './ServizioAutocomplete';   // R8-AGG — listino → riga
import { puoModificare, etichettaCantiere, aliquotaNormalizzata, opzioniAliquota } from './preventiviAzioni';   // FE-098

// Config invio documento al cliente (modali condivise) — vedi InvioDocumentoModals.jsx
const PREVENTIVO_INVIO_CONFIG = {
  oggettoLabel: 'Preventivo',
  filePrefix: 'Preventivo',
  idsKey: 'preventivo_ids',
  emailCliente: (d) => d.cliente_email || '',
  nomeCliente: (d) => d.cliente_nome || 'il cliente',
  importo: (d) => d.totale,
  messaggioSingolo: (d) => {
    const o = (d.titolo || '').trim();
    // Guardia numero uniforme (come proforma): mai "n. null" al cliente.
    const num = d.numero ? ` n. ${d.numero}` : '';
    return `in allegato trova il preventivo${num}${o ? ` relativo a ${o}` : ''}. Resto a disposizione per qualsiasi chiarimento.`;
  },
  messaggioMultiplo: 'in allegato trova il preventivo. Resto a disposizione per qualsiasi chiarimento.',
  singoloOkText: (dest) => `Preventivo inviato a ${dest}`,
  multiOkText: (n) => `${n} ${n === 1 ? 'preventivo inviato' : 'preventivi inviati'} ai clienti`,
  inviaCliente: preventiviApi.inviaCliente,
  inviaClienti: preventiviApi.inviaClienti,
};

const fmtEur = (n) => n == null ? '—' : `€ ${Number(n).toLocaleString('it-IT', { minimumFractionDigits: 2 })}`;
const fmtData = (d) => { if (!d) return '—'; const [y, m, g] = d.split('-'); return `${g}/${m}/${y}`; };

// R1 — dati scadenziario per il PDF: unisce `scadenziario` (importi risolti
// server-side, resto incluso) con `results` (porta il `valore`/% per la colonna
// Modalità). Ritorna null se non ci sono righe (sezione PDF omessa).
const buildPdfScadenziario = (pag) => {
  const scad = pag?.scadenziario;
  if (!scad?.righe?.length) return null;
  const valById = Object.fromEntries((pag.results || []).map((r) => [String(r.id), r.valore]));
  return { ...scad, righe: scad.righe.map((r) => ({ ...r, valore: valById[String(r.id)] })) };
};

// Helper visivi condivisi (design system)
const CARD_SHADOW = '0 6px 18px rgba(35,38,47,0.08)';
const ROW_BG = (i) => (i % 2 === 0 ? 'var(--ds-bg)' : 'var(--chip)');
const ROW_HOVER = 'color-mix(in srgb, var(--brand) 9%, var(--ds-bg))';
const tint = (c, pct) => `color-mix(in srgb, ${c} ${pct}%, transparent)`;

// Stato preventivo → token semantico/zona (bg via color-mix)
const STATO_META = {
  bozza:      { label: 'Bozza',      color: 'var(--ds-muted)' },
  inviato:    { label: 'Inviato',    color: 'var(--warn)' },
  accettato:  { label: 'Accettato',  color: 'var(--ok)' },
  convertito: { label: 'Convertito', color: 'var(--zone-blue)' },
  rifiutato:  { label: 'Rifiutato',  color: 'var(--ds-danger)' },
  scaduto:    { label: 'Scaduto',    color: 'var(--zone-violet)' },
};
const STATI_LIST = ['bozza', 'inviato', 'accettato', 'convertito', 'rifiutato', 'scaduto'];

const ALIQUOTE_IVA = [{ v: 0, l: 'Esente 0%' }, { v: 4, l: '4%' }, { v: 5, l: '5%' }, { v: 10, l: '10%' }, { v: 22, l: '22%' }];
const MODALITA_PAG = [
  { v: 'bonifico', l: 'Bonifico bancario' }, { v: 'contanti', l: 'Contanti' },
  { v: 'assegno', l: 'Assegno' }, { v: 'riba', l: 'RiBa' }, { v: 'pos', l: 'POS / Carta' },
];
const _default30 = () => { const d = new Date(); d.setDate(d.getDate() + 30); return d.toISOString().split('T')[0]; };
const FORM_EMPTY = {
  cliente: '', cantiere: '', titolo: '', descrizione: '', tipo_lavoro: '',
  aliquota_iva: '22', modalita_pagamento: 'bonifico',
  sconto_percentuale: '',  // FE-076 — '' = nessuno sconto
  data_emissione: new Date().toISOString().split('T')[0],
  data_scadenza: _default30(), note_cliente: '', note_interne: '',
};
// FE-076 — sconto opzionale: 5..100 a salti di 5 (nessuna opzione "0%")
const SCONTO_OPZIONI = Array.from({ length: 20 }, (_, i) => (i + 1) * 5);
const RIGA_EMPTY = { descrizione: '', quantita: '1', prezzo_unitario: '' };

function StatoBadge({ stato }) {
  const m = STATO_META[stato] || { label: stato, color: 'var(--ds-muted)' };
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(m.color, 14), color: m.color }}>{m.label}</span>;
}

function Modal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--ds-border)' }}>
          <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)' }}>{title}</h2>
          <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--ds-muted)', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>
        <div style={{ padding: '24px 28px' }}>{children}</div>
      </div>
    </div>
  );
}

function PreventivoForm({ initial, clientiList, cantieriList, onSave, onCancel, saving }) {
  // FE-098 — l'aliquota del server («22.00») nella forma delle voci del menu («22»).
  const [form, setForm] = useState({ ...FORM_EMPTY, ...initial, aliquota_iva: aliquotaNormalizzata(initial?.aliquota_iva) });
  const [righe, setRighe] = useState(initial?.righe?.length ? initial.righe.map((r) => ({ descrizione: r.descrizione, quantita: String(r.quantita), prezzo_unitario: String(r.prezzo_unitario) })) : [{ ...RIGA_EMPTY }]);
  const [pagamenti, setPagamenti] = useState(
    initial?.pagamenti?.length
      ? initial.pagamenti.map((p) => ({ etichetta: p.etichetta || '', modalita: p.modalita || 'percentuale', valore: p.modalita === 'resto' ? '' : String(p.valore ?? '') }))
      : []
  );
  const [cantiereBanner, setCantiereBanner] = useState(null);
  const [errore, setErrore] = useState('');
  const [mostraNota, setMostraNota] = useState(() => localStorage.getItem('preventivo_mostra_nota') === 'true');
  const toggleMostraNota = (val) => { setMostraNota(val); localStorage.setItem('preventivo_mostra_nota', String(val)); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };

  // Ricalcolo totali realtime (solo display: il totale autorevole è
  // ricalcolato server-side con Decimal).
  // FE-038 — parse sicuro: scarta NaN/Infinity. FE-025 — arrotonda a 2 decimali
  // per evitare artefatti float (es. 0.1+0.2).
  const numVal = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
  const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const aliquota = numVal(form.aliquota_iva);
  // FE-076 — lo sconto abbassa l'imponibile; l'IVA si calcola sul netto scontato
  // (stesso ordine del backend _ricalcola_totali). '' o 0 = nessuno sconto.
  const sconto = numVal(form.sconto_percentuale);
  const imponibileLordo = round2(righe.reduce((sum, r) => sum + numVal(r.quantita) * numVal(r.prezzo_unitario), 0));
  const scontoImporto = round2(imponibileLordo * (sconto / 100));
  const imponibile = round2(imponibileLordo - scontoImporto);
  const iva = round2(imponibile * (aliquota / 100));
  const totale = round2(imponibile + iva);

  // Gestione righe
  const setRiga = (i, k, v) => setRighe((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRiga = () => setRighe((rs) => [...rs, { ...RIGA_EMPTY }]);
  const removeRiga = (i) => {
    if (righe.length <= 1) return;
    const r = righe[i];
    if (r.descrizione || r.prezzo_unitario) {
      if (!window.confirm('Eliminare questa riga?')) return;
    }
    setRighe((rs) => rs.filter((_, idx) => idx !== i));
  };

  // R1 — scadenziario pagamenti (ORTOGONALE al fiscale): base = TOTALE LORDO
  const PAG_EMPTY = { etichetta: '', modalita: 'percentuale', valore: '' };
  const setPag = (i, k, v) => setPagamenti((ps) => ps.map((p, idx) => idx === i ? { ...p, [k]: v } : p));
  const addPag = () => setPagamenti((ps) => [...ps, { ...PAG_EMPTY }]);
  const removePag = (i) => setPagamenti((ps) => ps.filter((_, idx) => idx !== i));
  const risolviPag = (p) => {
    if (p.modalita === 'importo_fisso') return round2(numVal(p.valore));
    if (p.modalita === 'percentuale') return round2(totale * numVal(p.valore) / 100);
    return 0; // resto: calcolato a parte
  };
  const pagResto = pagamenti.filter((p) => p.modalita === 'resto');
  const scadSommaNonResto = round2(pagamenti.filter((p) => p.modalita !== 'resto').reduce((s, p) => s + risolviPag(p), 0));
  const scadResto = pagResto.length ? round2(totale - scadSommaNonResto) : 0;
  const scadSomma = round2(scadSommaNonResto + Math.max(scadResto, 0));
  const scadErrori = [];
  if (pagResto.length > 1) scadErrori.push('Ammessa al massimo una riga "resto".');
  if (pagResto.length && scadResto < -0.001) scadErrori.push('Le righe superano il totale: resto negativo.');
  if (!pagResto.length && pagamenti.length && Math.abs(scadSommaNonResto - totale) > 0.01 * pagamenti.length)
    scadErrori.push('Le righe non sommano al totale.');
  const scadValido = pagamenti.length > 0 && scadErrori.length === 0;

  // Banner import cantiere
  const onCantiereChange = (id) => {
    set('cantiere', id);
    if (id) {
      const c = cantieriList.find((x) => x.id === id);
      if (c && c.note) setCantiereBanner(c);
      else setCantiereBanner(null);
    } else { setCantiereBanner(null); }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const righeValide = righe.filter((r) => r.descrizione.trim() && parseFloat(r.prezzo_unitario) > 0);
    if (righeValide.length === 0) { setErrore('Aggiungi almeno una riga con descrizione e prezzo.'); return; }
    // Scadenziario (opzionale): se presente, deve quadrare col totale lordo.
    const pagValidi = pagamenti.filter((p) => p.modalita === 'resto' || numVal(p.valore) > 0 || p.etichetta.trim());
    if (pagValidi.length && !scadValido) { setErrore(scadErrori[0] || 'Scadenziario non valido.'); return; }
    setErrore('');
    onSave(form, righeValide, pagValidi);
  };

  return (
    <form onSubmit={handleSubmit}>
      {errore && <div style={{ marginBottom: 12, padding: '10px 14px', background: tint('var(--ds-danger)', 10), border: `1px solid ${tint('var(--ds-danger)', 30)}`, borderRadius: 'var(--radius-base)', fontSize: 13, color: 'var(--ds-danger)' }}>{errore}</div>}

      {/* Titolo */}
      <div style={{ marginBottom: 16 }}><label style={lbl}>Titolo preventivo *</label>
        <input required style={inp} value={form.titolo} onChange={(e) => set('titolo', e.target.value)} placeholder="Es. Ristrutturazione bagno cliente Rossi" /></div>

      {/* Cliente + Cantiere */}
      <div style={row2}>
        <div><label style={lbl}>Cliente</label>
          <select style={inp} value={form.cliente} onChange={(e) => set('cliente', e.target.value)}>
            <option value="">— Nessuno —</option>
            {clientiList.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select></div>
        <div><label style={lbl}>Cantiere</label>
          <select style={inp} value={form.cantiere} onChange={(e) => onCantiereChange(e.target.value)}>
            <option value="">— Nessuno —</option>
            {cantieriList.map((c) => <option key={c.id} value={c.id}>{c.titolo}</option>)}
          </select></div>
      </div>

      {/* Banner import cantiere — entrambi i bottoni chiudono il banner;
          "Importa" copia le note del cantiere nella descrizione lavori. */}
      {cantiereBanner && (
        <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--chip)', border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)' }}>
          <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6 }}>Importare la descrizione lavori da <strong>{cantiereBanner.titolo}</strong>?</div>
          <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginBottom: 8, lineHeight: 1.5 }}>{cantiereBanner.note?.slice(0, 150)}{cantiereBanner.note?.length > 150 ? '…' : ''}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => { set('descrizione', cantiereBanner.note); setCantiereBanner(null); }}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--zone-blue)', color: '#fff', cursor: 'pointer', fontWeight: 500 }}>Importa</button>
            <button type="button" onClick={() => setCantiereBanner(null)}
              style={{ fontSize: 12, padding: '6px 14px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer' }}>Salta</button>
          </div>
        </div>
      )}

      {/* Tipo lavoro */}
      <div style={{ marginBottom: 16 }}><label style={lbl}>Tipo lavoro</label>
        <input style={inp} value={form.tipo_lavoro} onChange={(e) => set('tipo_lavoro', e.target.value)} placeholder="Es. Ristrutturazione, Impianto elettrico…" /></div>

      {/* Descrizione lavori */}
      <div style={{ marginBottom: 16 }}><label style={lbl}>Descrizione lavori</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 70 }} value={form.descrizione} onChange={(e) => set('descrizione', e.target.value)} placeholder="Descrivi i lavori inclusi nel preventivo…" /></div>

      {/* Righe preventivo */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <label style={lbl}>Righe preventivo *</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ds-muted)', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={mostraNota} onChange={(e) => toggleMostraNota(e.target.checked)} />
            Descrizione estesa
          </label>
        </div>
        <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--chip)' }}>
              {['Servizio', 'Qtà', 'Prezzo unit.', 'Totale', ''].map((h) => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {righe.map((r, i) => {
                const tot = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
                return (
                  <tr key={i} style={{ borderTop: '1px solid var(--ds-border)' }}>
                    <td style={{ padding: '6px 8px', verticalAlign: mostraNota ? 'top' : 'middle' }}>
                      <ServizioAutocomplete
                        value={r.descrizione}
                        expanded={mostraNota}
                        inputStyle={{ ...inp, padding: '7px 10px', fontSize: 13 }}
                        onText={(t) => setRiga(i, 'descrizione', t)}
                        onPick={(v) => { setRiga(i, 'descrizione', v.descrizione); setRiga(i, 'prezzo_unitario', String(v.prezzo_unitario)); }}
                      />
                      {mostraNota && (
                        <div style={{ fontSize: 11, color: r.descrizione.length > 480 ? 'var(--ds-danger)' : 'var(--ds-muted)', textAlign: 'right', marginTop: 2 }}>{r.descrizione.length}/500</div>
                      )}</td>
                    <td style={{ padding: '6px 8px', width: 80, verticalAlign: mostraNota ? 'top' : 'middle' }}>
                      <input type="number" min="0" step="0.01" style={{ ...inp, padding: '7px 10px', fontSize: 13, textAlign: 'right' }} value={r.quantita} onChange={(e) => setRiga(i, 'quantita', e.target.value)} /></td>
                    <td style={{ padding: '6px 8px', width: 120, verticalAlign: mostraNota ? 'top' : 'middle' }}>
                      <input type="number" min="0" step="0.01" style={{ ...inp, padding: '7px 10px', fontSize: 13, textAlign: 'right' }} value={r.prezzo_unitario} onChange={(e) => setRiga(i, 'prezzo_unitario', e.target.value)} placeholder="0,00" /></td>
                    <td style={{ padding: '6px 10px', width: 100, fontSize: 13, fontWeight: 500, color: 'var(--ink)', textAlign: 'right', verticalAlign: mostraNota ? 'top' : 'middle' }}>{tot > 0 ? fmtEur(tot) : '—'}</td>
                    <td style={{ padding: '6px 8px', width: 44, verticalAlign: mostraNota ? 'top' : 'middle' }}>
                      {righe.length > 1 && (
                        <button type="button" onClick={() => removeRiga(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', width: 44, height: 44, color: 'var(--ds-muted)' }}>
                          <Trash2 size={16} strokeWidth={2} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: '8px 10px', borderTop: '1px solid var(--ds-border)' }}>
            <button type="button" onClick={addRiga} style={{ fontSize: 13, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, padding: '4px 0' }}>+ Aggiungi riga</button>
          </div>
        </div>
      </div>

      {/* IVA + Modalità pagamento */}
      <div style={row2}>
        <div><label style={lbl}>Aliquota IVA</label>
          <select style={inp} value={form.aliquota_iva} onChange={(e) => set('aliquota_iva', e.target.value)} data-testid="preventivo-aliquota">
            {opzioniAliquota(ALIQUOTE_IVA, form.aliquota_iva).map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
          </select></div>
        <div><label style={lbl}>Modalità pagamento</label>
          <select style={inp} value={form.modalita_pagamento} onChange={(e) => set('modalita_pagamento', e.target.value)}>
            {MODALITA_PAG.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
          </select></div>
      </div>

      {/* FE-076 — Sconto al cliente (opzionale). Vuoto = nessuno sconto. */}
      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>Sconto al cliente <span style={{ color: 'var(--ds-muted)', fontWeight: 400 }}>(opzionale)</span></label>
        <select
          style={inp}
          value={form.sconto_percentuale ? String(form.sconto_percentuale) : ''}
          onChange={(e) => set('sconto_percentuale', e.target.value)}
        >
          <option value="">Nessuno sconto</option>
          {SCONTO_OPZIONI.map((s) => <option key={s} value={String(s)}>{s}%</option>)}
        </select>
      </div>

      {/* Banner totali */}
      {imponibileLordo > 0 && (
        <div style={{ marginBottom: 16, display: 'flex', borderRadius: 'var(--radius-base)', overflow: 'hidden', border: '1px solid var(--ds-border)' }}>
          <div style={{ width: 3, background: 'var(--brand)', flexShrink: 0 }} />
          <div style={{ flex: 1, padding: '12px 16px', background: 'var(--chip)' }}>
            {sconto > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ds-muted)', marginBottom: 4 }}>
                  <span>Subtotale</span><span>{fmtEur(imponibileLordo)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--brand)', marginBottom: 4 }}>
                  <span>Sconto {sconto}%</span><span>-{fmtEur(scontoImporto)}</span>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ds-muted)', marginBottom: 4 }}>
              <span>Imponibile</span><span>{fmtEur(imponibile)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--ds-muted)', marginBottom: 6 }}>
              <span>IVA ({form.aliquota_iva}%)</span><span>{fmtEur(iva)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, fontSize: 16, color: 'var(--ink)', borderTop: '1px solid var(--ds-border)', paddingTop: 8 }}>
              <span>Totale</span><span>{fmtEur(totale)}</span>
            </div>
          </div>
        </div>
      )}

      {/* R1 — Scadenziario pagamenti (opzionale) */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label style={lbl}>Scadenziario pagamenti <span style={{ fontWeight: 400, color: 'var(--ds-muted)' }}>(opzionale)</span></label>
          <button type="button" onClick={addPag} style={{ fontSize: 13, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, padding: '4px 0' }}>+ Aggiungi scadenza</button>
        </div>
        {pagamenti.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--ds-muted)' }}>Nessuna scadenza: pagamento in un'unica soluzione. Aggiungi acconto / SAL / saldo per suddividere l'importo.</div>
        ) : (
          <>
            {pagamenti.map((p, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 128px 100px 92px 26px', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                <input style={{ ...inp, padding: '7px 10px' }} placeholder="Etichetta (es. Acconto)" value={p.etichetta} onChange={(e) => setPag(i, 'etichetta', e.target.value)} />
                <select style={{ ...inp, padding: '7px 10px' }} value={p.modalita} onChange={(e) => setPag(i, 'modalita', e.target.value)}>
                  <option value="percentuale">% del totale</option>
                  <option value="importo_fisso">Importo €</option>
                  <option value="resto">Resto / Saldo</option>
                </select>
                {p.modalita === 'resto'
                  ? <div style={{ fontSize: 12, color: 'var(--ds-muted)', textAlign: 'right' }}>calcolato</div>
                  : <input style={{ ...inp, padding: '7px 10px', textAlign: 'right' }} type="number" step="0.01" min="0" placeholder={p.modalita === 'percentuale' ? '%' : '€'} value={p.valore} onChange={(e) => setPag(i, 'valore', e.target.value)} />}
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', textAlign: 'right', whiteSpace: 'nowrap' }}>{fmtEur(p.modalita === 'resto' ? Math.max(scadResto, 0) : risolviPag(p))}</div>
                <button type="button" onClick={() => removePag(i)} aria-label="Rimuovi scadenza" style={{ background: 'none', border: 'none', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 17, lineHeight: 1, padding: 0 }}>×</button>
              </div>
            ))}
            {/* Banner live — base della % ESPLICITA a schermo (GATE 4) */}
            <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 'var(--radius-base)', background: tint(scadValido ? 'var(--ok)' : 'var(--warn)', 10), border: `1px solid ${tint(scadValido ? 'var(--ok)' : 'var(--warn)', 30)}`, fontSize: 12.5 }}>
              <div style={{ color: 'var(--ds-muted)', marginBottom: 3 }}>Le percentuali sono calcolate sul <b style={{ color: 'var(--ink)' }}>totale — IVA inclusa</b> ({fmtEur(totale)}).</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, color: 'var(--ink)' }}>
                <span>Somma scadenze: {fmtEur(scadSomma)}{pagResto.length ? ` · resto ${fmtEur(Math.max(scadResto, 0))}` : ''}</span>
                <span style={{ color: scadValido ? 'var(--ok)' : 'var(--warn)' }}>{scadValido ? '✓ quadra col totale' : (scadErrori[0] || 'non quadra col totale')}</span>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Date */}
      <div style={row2}>
        <div><label style={lbl}>Data emissione</label>
          <input type="date" style={inp} value={form.data_emissione} onChange={(e) => set('data_emissione', e.target.value)} /></div>
        <div><label style={lbl}>Valido fino al</label>
          <input type="date" style={inp} value={form.data_scadenza} onChange={(e) => set('data_scadenza', e.target.value)} /></div>
      </div>

      {/* Note */}
      <div style={{ marginBottom: 24 }}><label style={lbl}>Note per il cliente</label>
        <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={form.note_cliente} onChange={(e) => set('note_cliente', e.target.value)} /></div>

      {/* Bottoni */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button type="submit" disabled={saving} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>{saving ? 'Salvataggio…' : 'Salva preventivo'}</button>
      </div>
    </form>
  );
}

// Feature2 — Modal "Nuovo cantiere da preventivo" (pre-compilato dalle righe)
function ApriCantiereModal({ preventivo, onClose, onCreated }) {
  const [titolo, setTitolo] = useState(preventivo.titolo || '');
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');
  const [giaApertoId, setGiaApertoId] = useState(null);
  // CAP-018 — «Apri cantiere» oltre il limite di piano: modale dedicato.
  const [limiteInfo, setLimiteInfo] = useState(null);
  // DBT-157 — nome del cantiere già usato: modale dedicato.
  const [nomeInfo, setNomeInfo] = useState(null);

  const eur = (x) => Number(x || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const righe = preventivo.righe || [];

  const conferma = async () => {
    setBusy(true); setErrore(''); setGiaApertoId(null);
    try {
      const r = await preventiviApi.apriCantiere(preventivo.id, { titolo: titolo.trim() });
      const cantiereId = r.data.cantiere_id;
      // Best-effort: genera il PDF del preventivo (react-pdf) e caricalo come
      // Documento del cantiere appena creato. Se fallisce, il cantiere resta creato.
      try {
        const pdfData = await preventiviApi.getPdfData(preventivo.id);
        const pag = await preventiviApi.getPagamenti(preventivo.id).catch(() => null);
        const pdfDataFull = { ...pdfData, pagamenti: buildPdfScadenziario(pag) };
        const blob = await pdf(<PreventivoPDF data={pdfDataFull} />).toBlob();
        const fd = new FormData();
        const nomeFile = `Preventivo-${pdfData?.preventivo?.numero || preventivo.numero || 'preventivo'}.pdf`;
        fd.append('file', new File([blob], nomeFile, { type: 'application/pdf' }));
        fd.append('tipo', 'preventivo');
        fd.append('cantiere', cantiereId);
        await documentiApi.upload(fd);
      } catch { /* documento best-effort */ }
      onCreated(cantiereId);
    } catch (e) {
      const limite = limiteCantieriDaErrore(e);
      // DBT-157 — PRIMA del 409 «già aperto»: lo stato è lo stesso, il codice no.
      const ng = nomeGiaPresenteDaErrore(e);
      if (limite) {
        setLimiteInfo(limite);
      } else if (ng) {
        setNomeInfo(ng);
      } else if (e.response?.status === 409) {
        setGiaApertoId(e.response.data?.cantiere_id || null);
        setErrore('Cantiere già aperto per questo preventivo.');
      } else {
        setErrore(e.response?.data?.detail || "Errore durante l'apertura del cantiere.");
      }
    } finally { setBusy(false); }
  };

  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };

  return (
    <>
    <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onChiudiModulo={onClose} />
    <Modal title="Nuovo cantiere da preventivo" onClose={busy ? () => {} : onClose}>
      <div style={{ marginBottom: 16 }}>
        <div style={lbl}>Nome cantiere</div>
        <input
          value={titolo} onChange={(e) => setTitolo(e.target.value)} autoFocus data-campo-nome="true"
          style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--ds-bg)', color: 'var(--ink)' }}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div><div style={lbl}>Cliente</div><div style={{ fontSize: 14 }}>{preventivo.cliente_nome || '—'}</div></div>
        <div><div style={lbl}>Valore commessa</div><div style={{ fontSize: 14, fontWeight: 500 }}>{eur(preventivo.imponibile)}</div></div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={lbl}>Fasi del piano lavori ({righe.length})</div>
        <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden' }}>
          {righe.length === 0 ? (
            <div style={{ padding: 12, fontSize: 13, color: 'var(--ds-muted)' }}>Nessuna riga — il piano sarà vuoto, lo completi in app.</div>
          ) : righe.map((r, i) => (
            <div key={r.id || i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 12px', borderTop: i === 0 ? 'none' : '1px solid var(--ds-border)', fontSize: 13 }}>
              <span style={{ color: 'var(--ink)' }}>{r.descrizione || 'Fase'}</span>
              <span style={{ color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>
                {Number(r.totale_riga) > 0 ? eur(r.totale_riga) : 'da definire'}
              </span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--ds-muted)', marginTop: 6 }}>
          Le date delle fasi partono da oggi; le completi nel piano lavori del cantiere.
        </div>
      </div>

      {errore && (
        <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>
          {errore}
          {giaApertoId && (
            <button onClick={() => onCreated(giaApertoId)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--brand)', fontWeight: 500, cursor: 'pointer', fontSize: 13 }}>
              Vai al cantiere ↗
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onClose} disabled={busy} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: busy ? 'default' : 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
        <button type="button" onClick={conferma} disabled={busy} style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: busy ? 'var(--ds-border)' : 'var(--brand)', color: '#fff', cursor: busy ? 'default' : 'pointer', fontSize: 14, fontWeight: 500 }}>
          {busy ? 'Creazione…' : 'Crea cantiere'}
        </button>
      </div>
    </Modal>
    <LimiteCantieriModal info={limiteInfo} onClose={() => setLimiteInfo(null)} />
    </>
  );
}

function PreventivoDetail({ preventivo, onClose, onEdit, onDelete, onRefresh, onToast }) {
  const navigate = useNavigate();
  const [showApri, setShowApri] = useState(false);
  const [showInviaCliente, setShowInviaCliente] = useState(false);
  const [clienteErr, setClienteErr] = useState('');
  const [acting, setActing] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const lbl = { fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' };
  const val = { fontSize: 14, color: 'var(--ink)', marginTop: 2 };

  const handlePreparaPdf = async () => {
    setLoadingPdf(true);
    try {
      // pdf-data (righe/cliente/azienda) + scadenziario da /pagamenti/ (gia' in
      // prod, stessa fonte del blocco read-only e dell'editor).
      const [data, pag] = await Promise.all([
        preventiviApi.getPdfData(preventivo.id),
        preventiviApi.getPagamenti(preventivo.id).catch(() => null),
      ]);
      setPdfData({ ...data, pagamenti: buildPdfScadenziario(pag) });
    } catch { alert('Errore nel caricamento dati PDF'); }
    finally { setLoadingPdf(false); }
  };

  const azione = async (fn) => {
    setActing(true);
    try { await fn(); onRefresh(); onClose(); }
    catch (e) { alert(e.response?.data?.error || 'Errore'); }
    finally { setActing(false); }
  };

  const actionBtn = (bg) => ({ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: 'none', background: bg, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 });

  return (
    <Modal title={preventivo.numero ? `Preventivo ${preventivo.numero}` : preventivo.titolo} onClose={onClose}>
      {/* Azioni */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {/* FE-077, FE-098 — modificabile finché non è diventato un cantiere (stato
            «convertito»): una bozza solo collegata a un cantiere resta modificabile. */}
        {puoModificare(preventivo) && (
          <button onClick={() => onEdit(preventivo)} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}><Pencil size={15} strokeWidth={2} /> Modifica</button>
        )}
        {preventivo.stato === 'bozza' && (
          <button disabled={acting} onClick={() => azione(() => preventiviApi.invia(preventivo.id))} style={actionBtn('var(--warn)')}><Send size={15} strokeWidth={2} /> Segna inviato</button>
        )}
        {/* Invio email reale del PDF al cliente (in qualsiasi stato; aggiorna bozza→inviato lato backend) */}
        <button onClick={() => setShowInviaCliente(true)} style={{ ...actionBtn('var(--brand)'), flex: 'unset', padding: '9px 16px' }}><Mail size={15} strokeWidth={2} /> Invia al cliente</button>
        {preventivo.stato === 'inviato' && <>
          <button disabled={acting} onClick={() => azione(() => preventiviApi.accetta(preventivo.id))} style={actionBtn('var(--ok)')}><Check size={15} strokeWidth={2} /> Accettato</button>
          <button disabled={acting} onClick={() => azione(() => preventiviApi.rifiuta(preventivo.id))} style={actionBtn('var(--ds-danger)')}><XCircle size={15} strokeWidth={2} /> Rifiutato</button>
        </>}
        {preventivo.stato === 'accettato' && !preventivo.cantiere && (
          <button disabled={acting} onClick={() => {
            if (!preventivo.cliente) { setClienteErr('Associa un cliente al preventivo prima di aprire il cantiere.'); return; }
            setClienteErr(''); setShowApri(true);
          }} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            Apri cantiere
          </button>
        )}
        {preventivo.cantiere && (
          <button onClick={() => navigate(`/cantieri/${preventivo.cantiere}`)} style={{ flex: 1, padding: '9px 0', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ok)', 35)}`, background: tint('var(--ok)', 12), color: 'var(--ok)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            {etichettaCantiere(preventivo)}
          </button>
        )}
        <button onClick={() => { if (window.confirm('Eliminare questo preventivo?')) { preventiviApi.delete(preventivo.id).then(() => { onRefresh(); onClose(); }).catch(() => alert('Errore')); } }} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: `1px solid ${tint('var(--ds-danger)', 35)}`, background: tint('var(--ds-danger)', 12), color: 'var(--ds-danger)', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}><Trash2 size={16} strokeWidth={2} /></button>
        {!pdfData ? (
          <button onClick={handlePreparaPdf} disabled={loadingPdf} style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: '1px solid var(--zone-blue)', background: 'var(--ds-bg)', color: 'var(--zone-blue)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            {loadingPdf ? 'Preparazione...' : 'Genera PDF'}
          </button>
        ) : (
          <PDFDownloadLink
            document={<PreventivoPDF data={pdfData} />}
            fileName={`Preventivo-${pdfData.preventivo.numero}.pdf`}
            style={{ padding: '9px 16px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', textDecoration: 'none', fontSize: 13, fontWeight: 500, display: 'inline-flex', alignItems: 'center' }}>
            {({ loading }) => loading ? 'Generazione...' : 'Scarica PDF'}
          </PDFDownloadLink>
        )}
      </div>

      {clienteErr && (
        <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{clienteErr}</div>
      )}

      {showApri && (
        <ApriCantiereModal
          preventivo={preventivo}
          onClose={() => setShowApri(false)}
          onCreated={(cantiereId) => { setShowApri(false); onRefresh && onRefresh(); onClose(); navigate(`/cantieri/${cantiereId}`); }}
        />
      )}

      {showInviaCliente && (
        <InvioClienteModal
          documento={preventivo}
          config={PREVENTIVO_INVIO_CONFIG}
          onClose={() => setShowInviaCliente(false)}
          onToast={(t) => { onToast && onToast(t); onRefresh && onRefresh(); }}
        />
      )}

      {preventivo.titolo && <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginBottom: 16 }}>{preventivo.titolo}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div><div style={lbl}>Stato</div><div style={val}><StatoBadge stato={preventivo.stato} /></div></div>
        <div><div style={lbl}>Numero</div><div style={val}>{preventivo.numero || '—'}</div></div>
        <div><div style={lbl}>Cliente</div><div style={val}>{preventivo.cliente_nome || '—'}</div></div>
        <div><div style={lbl}>Cantiere</div><div style={val}>{preventivo.cantiere_nome || '—'}</div></div>
        <div><div style={lbl}>Emissione</div><div style={val}>{fmtData(preventivo.data_emissione)}</div></div>
        <div><div style={lbl}>Valido fino al</div><div style={val}>{fmtData(preventivo.data_scadenza)}</div></div>
        {preventivo.data_accettazione && <div><div style={lbl}>Data accettazione</div><div style={val}>{fmtData(preventivo.data_accettazione)}</div></div>}
      </div>

      {preventivo.descrizione && (
        <div style={{ marginBottom: 16, padding: 12, background: 'var(--chip)', borderRadius: 'var(--radius-base)' }}>
          <div style={lbl}>Descrizione lavori</div>
          <div style={{ fontSize: 14, color: 'var(--ink)', marginTop: 4, whiteSpace: 'pre-wrap' }}>{preventivo.descrizione}</div>
        </div>
      )}

      {/* Righe — scroll-x locale: con descrizioni lunghe a 375px la colonna
          Descrizione si strozzava e i prezzi andavano a capo. minWidth dà
          larghezza leggibile e swipe orizzontale; inerte su desktop (modale 640). */}
      {preventivo.righe?.length > 0 && (
        <div style={{ marginBottom: 16, border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', minWidth: 440, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: 'var(--chip)' }}>
              {['Descrizione', 'Qtà', 'Prezzo unit.', 'Totale'].map((h) => (
                <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', whiteSpace: h === 'Descrizione' ? 'normal' : 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {preventivo.righe.map((r) => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--ds-border)' }}>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--ink)' }}>{r.descrizione}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--ds-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.quantita}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, color: 'var(--ds-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>{parseFloat(r.prezzo_unitario) > 0 ? fmtEur(r.prezzo_unitario) : '—'}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 500, color: 'var(--ink)', textAlign: 'right', whiteSpace: 'nowrap' }}>{parseFloat(r.totale_riga) > 0 ? fmtEur(r.totale_riga) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Totali */}
      <div style={{ display: 'flex', borderRadius: 'var(--radius-base)', overflow: 'hidden', border: '1px solid var(--ds-border)' }}>
        <div style={{ width: 3, background: 'var(--brand)', flexShrink: 0 }} />
        <div style={{ flex: 1, padding: '14px 16px', background: 'var(--chip)' }}>
          {preventivo.sconto_percentuale ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: 'var(--ds-muted)' }}>
                <span>Subtotale</span><span>{fmtEur(preventivo.imponibile_lordo)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: 'var(--brand)' }}>
                <span>Sconto {preventivo.sconto_percentuale}%</span><span>-{fmtEur(preventivo.sconto_importo)}</span>
              </div>
            </>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 14, color: 'var(--ds-muted)' }}>
            <span>Imponibile</span><span>{fmtEur(preventivo.imponibile)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 14, color: 'var(--ds-muted)' }}>
            <span>IVA ({preventivo.aliquota_iva || 22}%)</span><span>{fmtEur(preventivo.iva_totale)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 500, fontSize: 18, color: 'var(--ink)', borderTop: '1px solid var(--ds-border)', paddingTop: 8, marginTop: 4 }}>
            <span>Totale preventivo</span><span>{fmtEur(preventivo.totale)}</span>
          </div>
        </div>
      </div>

      {/* R1 — scadenziario pagamenti (read-only) */}
      {preventivo.pagamenti?.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Scadenziario pagamenti</div>
          {preventivo.pagamenti.map((p) => {
            const base = parseFloat(preventivo.totale) || 0;
            const imp = p.modalita === 'percentuale' ? base * (parseFloat(p.valore) || 0) / 100
              : (p.modalita === 'importo_fisso' ? (parseFloat(p.valore) || 0) : null);
            const label = p.modalita === 'percentuale' ? `${parseFloat(p.valore)}% del totale`
              : (p.modalita === 'importo_fisso' ? 'importo fisso' : 'resto / saldo');
            return (
              <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '1px solid var(--ds-border)' }}>
                <span style={{ color: 'var(--ink)' }}>{p.etichetta || label} <span style={{ color: 'var(--ds-muted)', fontSize: 12 }}>· {label}</span></span>
                <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{imp != null ? fmtEur(imp) : '—'}</span>
              </div>
            );
          })}
          <div style={{ fontSize: 11.5, color: 'var(--ds-muted)', marginTop: 5 }}>Percentuali sul totale — IVA inclusa.</div>
        </div>
      )}
    </Modal>
  );
}

function ImportPreventivoPdfModal({ onClose, onImported, clientiList }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errore, setErrore] = useState('');
  const [confidenza, setConfidenza] = useState('');
  const [righeCount, setRigheCount] = useState(0);
  const [form, setForm] = useState({ ...FORM_EMPTY });
  const [righe, setRighe] = useState([{ ...RIGA_EMPTY }]);
  const [clienteEstratto, setClienteEstratto] = useState(null);
  const [saving, setSaving] = useState(false);
  // DBT-157 — il cliente letto dal PDF ha il nome di uno già presente.
  const [nomeInfo, setNomeInfo] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setRiga = (i, k, v) => setRighe((rs) => rs.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRiga = () => setRighe((rs) => [...rs, { ...RIGA_EMPTY }]);
  const removeRiga = (i) => { if (righe.length > 1) setRighe((rs) => rs.filter((_, idx) => idx !== i)); };

  const aliquota = parseFloat(form.aliquota_iva) || 0;
  const imponibile = righe.reduce((sum, r) => sum + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);
  const iva = imponibile * (aliquota / 100);
  const totale = imponibile + iva;

  const analizza = async () => {
    if (!file) return;
    setLoading(true);
    setErrore('');
    try {
      const r = await preventiviApi.importaPdf(file);
      const data = r.data;
      const e = data.estratto;
      setConfidenza(data.confidenza);
      setRigheCount(data.righe_count);
      setClienteEstratto(e.cliente || null);

      // Cerca cliente esistente per nome
      let clienteId = '';
      if (e.cliente?.nome) {
        // DBT-157 — stessa regola del backend: maiuscole e spazi non contano.
        const match = clientiList.find((c) => stessoNome(c.nome, e.cliente.nome));
        if (match) clienteId = match.id;
      }

      setForm({
        ...FORM_EMPTY,
        titolo: e.titolo || '',
        tipo_lavoro: e.tipo_lavoro || '',
        cliente: clienteId,
        aliquota_iva: aliquotaNormalizzata(e.aliquota_iva),
        modalita_pagamento: e.modalita_pagamento || 'bonifico',
        note_cliente: e.note_cliente || '',
        data_scadenza: e.data_scadenza || _default30(),
      });

      const righeEstratte = (e.righe || []).map((r, i) => ({
        descrizione: r.descrizione || '',
        quantita: String(r.quantita ?? 1),
        prezzo_unitario: String(r.prezzo_unitario ?? 0),
      }));
      setRighe(righeEstratte.length > 0 ? righeEstratte : [{ ...RIGA_EMPTY }]);
      setStep(2);
    } catch (err) {
      setErrore(err.response?.data?.detail || 'Errore durante l\'analisi del documento.');
    } finally {
      setLoading(false);
    }
  };

  const creaPreventivo = async () => {
    setSaving(true);
    setErrore('');
    try {
      // 1. Se cliente estratto e non trovato in lista, crealo
      let clienteId = form.cliente;
      if (!clienteId && clienteEstratto?.nome) {
        const clienteData = {
          nome: clienteEstratto.nome,
          tipo: clienteEstratto.piva ? 'azienda' : 'privato',
          piva: clienteEstratto.piva || '',
          codice_fiscale: clienteEstratto.codice_fiscale || '',
          email: clienteEstratto.email || '',
          telefono: clienteEstratto.telefono || '',
          indirizzo: clienteEstratto.indirizzo || '',
        };
        const cr = await clientiApi.create(clienteData);
        clienteId = cr.data.id;
      }

      // 2. Crea preventivo
      const payload = { ...form, cliente: clienteId || undefined };
      if (!payload.cliente) delete payload.cliente;
      if (!payload.cantiere) delete payload.cantiere;
      // FE-076 — '' (nessuno sconto) -> null (il backend rifiuta '' su un intero)
      payload.sconto_percentuale = payload.sconto_percentuale ? Number(payload.sconto_percentuale) : null;
      delete payload.imponibile; delete payload.iva_totale; delete payload.totale;
      const pr = await preventiviApi.create(payload);

      // 3. Crea righe
      const righeValide = righe.filter((r) => r.descrizione && parseFloat(r.prezzo_unitario) > 0);
      for (let i = 0; i < righeValide.length; i++) {
        await preventiviApi.addRiga(pr.data.id, {
          descrizione: righeValide[i].descrizione,
          quantita: parseFloat(righeValide[i].quantita) || 1,
          prezzo_unitario: parseFloat(righeValide[i].prezzo_unitario) || 0,
          ordine: i,
        });
      }

      onImported(pr.data);
      onClose();
    } catch (err) {
      const ng = nomeGiaPresenteDaErrore(err);
      if (ng) { setNomeInfo(ng); return; }
      setErrore('Errore nella creazione: ' + (err.response?.data ? JSON.stringify(err.response.data) : err.message));
    } finally {
      setSaving(false);
    }
  };

  const inp = { width: '100%', padding: '9px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' };
  const lbl = { display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--ds-muted)', marginBottom: 4 };
  const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 };
  const CONFIDENZA_META = {
    alta:  { label: 'Confidenza alta',  color: 'var(--ok)' },
    media: { label: 'Confidenza media', color: 'var(--warn)' },
    bassa: { label: 'Confidenza bassa', color: 'var(--ds-danger)' },
  };
  const confMeta = CONFIDENZA_META[confidenza] || CONFIDENZA_META.bassa;

  return (
    <>
    <NomeGiaPresenteModal info={nomeInfo} onClose={() => setNomeInfo(null)} onChiudiModulo={onClose} />
    <Modal title="Importa preventivo da PDF" onClose={onClose}>
      {step === 1 && (
        <div>
          <div
            onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--brand)'; }}
            onDragLeave={(e) => { e.currentTarget.style.borderColor = 'var(--ds-border)'; }}
            onDrop={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--ds-border)'; const f = e.dataTransfer.files[0]; if (f && f.type === 'application/pdf') setFile(f); }}
            style={{ border: '2px dashed var(--ds-border)', borderRadius: 'var(--radius-card)', padding: '40px 20px', textAlign: 'center', marginBottom: 20, cursor: 'pointer', transition: 'border-color 0.2s' }}
            onClick={() => document.getElementById('import-prev-pdf-input').click()}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: 'var(--ds-muted)' }}>
              <Upload size={36} strokeWidth={1.5} />
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink)', fontWeight: 500, marginBottom: 4 }}>
              {file ? file.name : 'Trascina un PDF o clicca per selezionare'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--ds-muted)' }}>Preventivi, offerte commerciali, computi metrici</div>
            <input id="import-prev-pdf-input" type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>Annulla</button>
            <button onClick={analizza} disabled={!file || loading}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: !file ? 'var(--ds-border)' : 'var(--brand)', color: '#fff', cursor: file ? 'pointer' : 'default', fontSize: 14, fontWeight: 500 }}>
              {loading ? 'mAIstro sta analizzando il preventivo...' : 'Analizza documento →'}
            </button>
          </div>
        </div>
      )}
      {step === 2 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 99, fontSize: 12, fontWeight: 500, background: tint(confMeta.color, 14), color: confMeta.color }}>{confMeta.label}</span>
            <span style={{ fontSize: 13, color: 'var(--ds-muted)' }}>{righeCount} voci trovate</span>
          </div>
          {errore && <div style={{ padding: '10px 14px', background: tint('var(--ds-danger)', 10), color: 'var(--ds-danger)', borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16 }}>{errore}</div>}

          {/* Cliente estratto */}
          {clienteEstratto?.nome && !form.cliente && (
            <div style={{ padding: '10px 14px', background: tint('var(--brand)', 10), borderRadius: 'var(--radius-base)', fontSize: 13, marginBottom: 16, borderLeft: '3px solid var(--brand)' }}>
              Cliente trovato nel documento: <span style={{ fontWeight: 500 }}>{clienteEstratto.nome}</span>
              {clienteEstratto.piva && <span style={{ color: 'var(--ds-muted)' }}> — P.IVA {clienteEstratto.piva}</span>}
              <div style={{ fontSize: 12, color: 'var(--ds-muted)', marginTop: 2 }}>Verra creato automaticamente al salvataggio</div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={lbl}>Titolo *</label>
            <input style={inp} value={form.titolo} onChange={(e) => set('titolo', e.target.value)} />
          </div>
          <div style={row2}>
            <div><label style={lbl}>Cliente esistente (opzionale)</label>
              <select style={inp} value={form.cliente} onChange={(e) => set('cliente', e.target.value)}>
                <option value="">{clienteEstratto?.nome ? '— Crea nuovo dal PDF —' : '— Seleziona —'}</option>
                {clientiList.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </select></div>
            <div><label style={lbl}>Tipo lavoro</label>
              <input style={inp} value={form.tipo_lavoro} onChange={(e) => set('tipo_lavoro', e.target.value)} /></div>
          </div>

          {/* Righe */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ ...lbl, marginBottom: 0 }}>Voci preventivo</label>
              <button onClick={addRiga} type="button" style={{ fontSize: 12, color: 'var(--brand)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>+ Aggiungi riga</button>
            </div>
            <div style={{ border: '1px solid var(--ds-border)', borderRadius: 'var(--radius-base)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: 'var(--chip)' }}>
                  {['Descrizione', 'Qta', 'Prezzo unit.', 'Totale', ''].map((h) => (
                    <th key={h} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {righe.map((r, i) => {
                    const tot = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
                    return (
                      <tr key={i} style={{ borderTop: '1px solid var(--ds-border)' }}>
                        <td style={{ padding: '6px 8px' }}><input style={{ ...inp, padding: '6px 8px', fontSize: 13 }} value={r.descrizione} onChange={(e) => setRiga(i, 'descrizione', e.target.value)} /></td>
                        <td style={{ padding: '6px 8px', width: 70 }}><input style={{ ...inp, padding: '6px 8px', fontSize: 13, textAlign: 'right' }} value={r.quantita} onChange={(e) => setRiga(i, 'quantita', e.target.value)} /></td>
                        <td style={{ padding: '6px 8px', width: 100 }}><input style={{ ...inp, padding: '6px 8px', fontSize: 13, textAlign: 'right' }} value={r.prezzo_unitario} onChange={(e) => setRiga(i, 'prezzo_unitario', e.target.value)} /></td>
                        <td style={{ padding: '6px 8px', width: 90, fontSize: 13, textAlign: 'right', color: 'var(--ink)' }}>{fmtEur(tot)}</td>
                        <td style={{ padding: '6px 4px', width: 36 }}>
                          {righe.length > 1 && <button onClick={() => removeRiga(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ds-danger)', fontSize: 16, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totali */}
          <div style={{ borderLeft: '3px solid var(--brand)', background: 'var(--chip)', borderRadius: '0 var(--radius-base) var(--radius-base) 0', padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 24 }}>
            <div><div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>Imponibile</div><div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}>{fmtEur(imponibile)}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>IVA ({aliquota}%)</div><div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)' }}>{fmtEur(iva)}</div></div>
            <div><div style={{ fontSize: 11, color: 'var(--ds-muted)', textTransform: 'uppercase' }}>Totale</div><div style={{ fontSize: 16, fontWeight: 500, color: 'var(--brand)' }}>{fmtEur(totale)}</div></div>
          </div>

          <div style={row2}>
            <div><label style={lbl}>Aliquota IVA</label>
              <select style={inp} value={form.aliquota_iva} onChange={(e) => set('aliquota_iva', e.target.value)}>
                {opzioniAliquota(ALIQUOTE_IVA, form.aliquota_iva).map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
              </select></div>
            <div><label style={lbl}>Modalita pagamento</label>
              <select style={inp} value={form.modalita_pagamento} onChange={(e) => set('modalita_pagamento', e.target.value)}>
                {MODALITA_PAG.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={lbl}>Note per il cliente</label>
            <textarea style={{ ...inp, resize: 'vertical', minHeight: 60 }} value={form.note_cliente} onChange={(e) => set('note_cliente', e.target.value)} /></div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
            <button onClick={() => { setStep(1); setErrore(''); }} style={{ padding: '10px 20px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>← Indietro</button>
            <button onClick={creaPreventivo} disabled={saving || !form.titolo}
              style={{ padding: '10px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
              {saving ? 'Creazione in corso...' : 'Crea preventivo'}
            </button>
          </div>
        </div>
      )}
    </Modal>
    </>
  );
}

// Coda ranking Preventivi: StatoBadge. Dominio locale — lo StatoBadge vive qui,
// mai nel componente condiviso.
function renderCodaPreventivi(row, { isMobile }) {
  if (isMobile) {
    return <div style={{ textAlign: 'right' }}><StatoBadge stato={row.stato} /></div>;
  }
  return <td style={{ padding: '11px 14px' }}><StatoBadge stato={row.stato} /></td>;
}

export default function Preventivi() {
  const isMobile = useIsMobile();
  const { isCards, mode, setMode } = useListMode('preventivi', isMobile);
  const [lista, setLista] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [clientiList, setClientiList] = useState([]);
  const [cantieriList, setCantieriList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Deep-link da Home: /preventivi?stato=bozza pre-applica il filtro (link condivisibile, back del browser).
  const [filtroStato, setFiltroStato] = useState(() => {
    const s = new URLSearchParams(window.location.search).get('stato');
    return STATI_LIST.includes(s) ? s : '';
  });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dettaglio, setDettaglio] = useState(null);
  const [showImportPdf, setShowImportPdf] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showInviaClienti, setShowInviaClienti] = useState(false);
  const [toast, setToast] = useState(null);
  const location = useLocation();

  const carica = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    try {
      const params = {};
      if (search) params.search = search;
      if (filtroStato) params.stato = filtroStato;
      const [listRes, kpiRes] = await Promise.all([preventiviApi.list(params), preventiviApi.kpi()]);
      setLista(listRes.data.results ?? listRes.data);
      setKpi(kpiRes.data);
    } catch {} finally { setLoading(false); }
  }, [search, filtroStato]);

  const toggleSel = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  useEffect(() => { carica(); }, [carica]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 6000); return () => clearTimeout(t); }, [toast]);
  useEffect(() => {
    clientiApi.list().then((r) => setClientiList(r.data.results ?? r.data)).catch(() => {});
    cantieriApi.list().then((r) => setCantieriList(r.data.results ?? r.data)).catch(() => {});
  }, []);

  // Apri form automaticamente se navigazione da Home/sidebar (+Nuovo Preventivo)
  useEffect(() => {
    if (location.pathname === '/preventivi/nuovo') { setEditing(null); setShowForm(true); }
  }, []); // eslint-disable-line

  // Deep-link da Home "Cosa fare oggi": /preventivi?apri={id} apre la scheda preventivo
  useEffect(() => {
    const apriId = new URLSearchParams(location.search).get('apri');
    if (apriId) preventiviApi.get(apriId).then(r => setDettaglio(r.data)).catch(() => {});
  }, [location.search]); // eslint-disable-line

  const apriDettaglio = async (p) => {
    try {
      const r = await preventiviApi.get(p.id);
      setDettaglio(r.data);
    } catch { setDettaglio(p); }
  };

  const salva = async (form, righeValide, pagamentiValidi = []) => {
    setSaving(true);
    let preventivoCreato = null;
    // R1 — scadenziario: 'resto' non porta valore; % / importo -> numero
    const pagPayload = (p, i) => ({
      etichetta: p.etichetta || '', modalita: p.modalita,
      valore: p.modalita === 'resto' ? 0 : (parseFloat(p.valore) || 0), ordine: i,
    });
    try {
      const payload = { ...form };
      if (!payload.cliente) delete payload.cliente;
      if (!payload.cantiere) delete payload.cantiere;
      if (!payload.data_scadenza) delete payload.data_scadenza;
      // FE-076 — sconto: '' (nessuno) -> null; altrimenti numero (il backend
      // rifiuta '' su un campo intero).
      payload.sconto_percentuale = payload.sconto_percentuale ? Number(payload.sconto_percentuale) : null;
      // Totali calcolati dal backend dopo POST righe — non servono nel payload
      delete payload.imponibile;
      delete payload.iva_totale;
      delete payload.totale;
      // FE-077 — modificare un preventivo lo riporta in "Bozza": il contenuto è
      // cambiato e va re-inviato (lo stato non deve mai mentire sull'ultima
      // versione). Su un nuovo preventivo lo stato non si invia (default backend).
      if (editing) payload.stato = 'bozza'; else delete payload.stato;

      if (editing) {
        await preventiviApi.update(editing.id, payload);
        // Per editing: elimina righe vecchie e ricrea
        if (editing.righe) {
          await Promise.all(editing.righe.map((r) => preventiviApi.deleteRiga(editing.id, r.id).catch(() => {})));
        }
        await Promise.all(righeValide.map((r, i) => preventiviApi.addRiga(editing.id, {
          descrizione: r.descrizione, quantita: parseFloat(r.quantita) || 1,
          prezzo_unitario: parseFloat(r.prezzo_unitario) || 0, ordine: i,
        })));
        // Scadenziario: rimpiazza (delete correnti + re-add)
        const oldPag = await preventiviApi.getPagamenti(editing.id).catch(() => ({ results: [] }));
        await Promise.all((oldPag.results || []).map((p) => preventiviApi.deletePagamento(editing.id, p.id).catch(() => {})));
        await Promise.all(pagamentiValidi.map((p, i) => preventiviApi.addPagamento(editing.id, pagPayload(p, i))));
      } else {
        const res = await preventiviApi.create(payload);
        preventivoCreato = res.data;
        // POST tutte le righe in parallelo con ordine esplicito
        await Promise.all(righeValide.map((r, i) => preventiviApi.addRiga(preventivoCreato.id, {
          descrizione: r.descrizione, quantita: parseFloat(r.quantita) || 1,
          prezzo_unitario: parseFloat(r.prezzo_unitario) || 0, ordine: i,
        })));
        await Promise.all(pagamentiValidi.map((p, i) => preventiviApi.addPagamento(preventivoCreato.id, pagPayload(p, i))));
      }
      setShowForm(false); setEditing(null); carica();
    } catch (e) {
      // Rollback: se il preventivo è stato creato ma le righe falliscono, elimina il preventivo
      if (preventivoCreato) {
        await preventiviApi.delete(preventivoCreato.id).catch(() => {});
      }
      alert('Errore: ' + (e.response?.data ? JSON.stringify(e.response.data) : e.message));
    } finally { setSaving(false); }
  };

  const elimina = async (id) => {
    try { await preventiviApi.delete(id); setDettaglio(null); carica(); }
    catch { alert('Errore nell\'eliminazione'); }
  };

  return (
    <div style={{ padding: '28px 20px 28px 32px', width: '100%', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 600, color: 'var(--ink)' }}>Preventivi</h1>
          <p style={{ margin: '4px 0 0', fontSize: 14, color: 'var(--ds-muted)' }}>Gestione offerte e pipeline commerciale</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={() => setShowImportPdf(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '11px 22px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', cursor: 'pointer', fontSize: 14, fontWeight: 500, color: 'var(--ds-text)' }}>
            <FileText size={15} strokeWidth={2} />
            Importa da PDF
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '11px 22px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500, boxShadow: '0 4px 14px rgba(46,160,132,0.35)' }}>+ Nuovo preventivo</button>
        </div>
      </div>

      {/* KPI */}
      {kpi && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 14, marginBottom: 28 }}>
          {[
            { label: 'Totale', value: kpi.totale, color: 'var(--brand)' },
            { label: 'Inviati', value: kpi.inviati, color: 'var(--zone-blue)' },
            { label: 'Accettati', value: kpi.accettati, color: 'var(--ok)' },
            { label: 'Convertiti', value: kpi.convertiti, color: 'var(--zone-acqua)' },
            { label: 'Rifiutati', value: kpi.rifiutati, color: 'var(--ds-danger)' },
            { label: 'Pipeline', value: fmtEur(kpi.valore_pipeline), color: 'var(--zone-violet)', big: true },
          ].map((s) => (
            <div key={s.label} style={{ background: 'var(--chip)', borderRadius: 'var(--radius-card)', padding: '20px 24px', border: '1px solid var(--ds-border)', borderTop: `3px solid ${s.color}`, boxShadow: CARD_SHADOW }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: s.big ? 18 : 24, fontWeight: 700, color: 'var(--ink)', marginTop: 4 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtri */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--ds-muted)', display: 'flex' }}><Search size={16} strokeWidth={2} /></span>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per titolo, cliente…"
            style={{ width: '100%', padding: '10px 12px 10px 36px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--ds-bg)', color: 'var(--ink)' }} />
        </div>
        <button onClick={() => setFiltroStato('')} style={{
          padding: '10px 16px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
          background: !filtroStato ? 'var(--brand)' : 'var(--ds-bg)',
          color: !filtroStato ? '#fff' : 'var(--ds-text)',
          border: !filtroStato ? '1px solid var(--brand)' : '1px solid var(--ds-border)',
        }}>Tutti</button>
        <select value={filtroStato} onChange={(e) => setFiltroStato(e.target.value)}
          style={{ padding: '10px 14px', borderRadius: 'var(--radius-base)', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-text)', outline: 'none', fontFamily: 'inherit' }}>
          <option value="">Filtra stato…</option>
          {STATI_LIST.map((s) => <option key={s} value={s}>{STATO_META[s].label}</option>)}
        </select>
        {!isMobile && (
          <div style={{ marginLeft: 'auto' }}>
            <ListModeToggle mode={mode} setMode={setMode} />
          </div>
        )}
      </div>

      {/* Toolbar selezione multipla */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16, padding: '12px 18px', borderRadius: 'var(--radius-card)', background: 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))', border: '1px solid var(--brand)', boxShadow: CARD_SHADOW }}>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)' }}>{selected.size} {selected.size === 1 ? 'preventivo selezionato' : 'preventivi selezionati'}</span>
          <button onClick={() => setShowInviaClienti(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <Mail size={15} strokeWidth={2} /> Invia ai clienti ({selected.size})
          </button>
          <button onClick={() => setSelected(new Set())} style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 12px', borderRadius: 'var(--radius-base)', border: '1px solid var(--ds-border)', background: 'var(--ds-bg)', color: 'var(--ds-muted)', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
            <X size={14} strokeWidth={2} /> Deseleziona
          </button>
        </div>
      )}

      {/* Lista + Ranking */}
      {(() => {
        const rankingPreventivi = [...lista]
          .sort((a, b) => parseFloat(b.totale || 0) - parseFloat(a.totale || 0))
          .slice(0, 5)
          .map((p, i) => ({
            pos: i + 1,
            cliente: p.cliente_nome || '—',
            totale: p.totale,
            stato: p.stato,
          }));
        const listaBody = (
          <>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--ds-muted)' }}>Caricamento…</div>
            ) : lista.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW }}>
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14, color: 'var(--ds-muted)' }}><FileText size={44} strokeWidth={1.5} /></div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 600, color: 'var(--ink)', marginBottom: 8 }}>Nessun preventivo trovato</div>
                  <div style={{ fontSize: 14, color: 'var(--ds-muted)', marginBottom: 24 }}>{search || filtroStato ? 'Prova a cambiare i filtri' : 'Crea il tuo primo preventivo'}</div>
                  {!search && !filtroStato && <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ padding: '11px 24px', borderRadius: 'var(--radius-base)', border: 'none', background: 'var(--brand)', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>+ Nuovo preventivo</button>}
                </div>
              ) : isCards ? (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: isMobile ? 10 : 14 }}>
                  {lista.map((p) => (
                    <DataCard
                      key={p.id}
                      title={p.titolo || p.numero || '—'}
                      subtitle={[p.cliente_nome, p.numero].filter(Boolean).join(' · ') || null}
                      onClick={() => apriDettaglio(p)}
                      badge={<StatoBadge stato={p.stato} />}
                      meta={[
                        { label: 'Imponibile', value: fmtEur(p.imponibile) },
                        { label: 'Scadenza', value: fmtData(p.data_scadenza) },
                        p.cantiere_nome ? { label: 'Cantiere', value: p.cantiere_nome } : null,
                      ]}
                    />
                  ))}
                </div>
              ) : (
                <div style={{ background: 'var(--ds-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--ds-border)', boxShadow: CARD_SHADOW, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--ds-border)' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, color: 'var(--ink)' }}>Elenco Preventivi</span>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr style={{ background: 'var(--chip)', borderBottom: '2px solid var(--ds-border)' }}>
                      <th style={{ padding: '12px 8px 12px 16px', width: 32 }}>
                        <input type="checkbox" aria-label="Seleziona tutti"
                          checked={lista.length > 0 && selected.size === lista.length}
                          ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < lista.length; }}
                          onChange={(e) => setSelected(e.target.checked ? new Set(lista.map((p) => p.id)) : new Set())}
                          style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
                      </th>
                      {['Numero', 'Titolo', 'Cliente', 'Cantiere', 'Imponibile', 'Emissione', 'Scadenza', 'Stato'].map((h) => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 500, color: 'var(--ds-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {lista.map((p, i) => (
                        <tr key={p.id} onClick={() => apriDettaglio(p)} style={{ borderTop: '1px solid var(--ds-border)', cursor: 'pointer', background: selected.has(p.id) ? 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))' : ROW_BG(i) }}
                          onMouseEnter={(e) => { if (!selected.has(p.id)) e.currentTarget.style.background = ROW_HOVER; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = selected.has(p.id) ? 'color-mix(in srgb, var(--brand) 10%, var(--ds-bg))' : ROW_BG(i); }}>
                          <td onClick={(e) => e.stopPropagation()} style={{ padding: '13px 8px 13px 16px' }}>
                            <input type="checkbox" aria-label={`Seleziona ${p.numero}`} checked={selected.has(p.id)} onChange={() => toggleSel(p.id)}
                              style={{ width: 16, height: 16, accentColor: 'var(--brand)', cursor: 'pointer' }} />
                          </td>
                          <td style={{ padding: '13px 16px', fontWeight: 500, fontSize: 14, color: 'var(--ink)' }}>{p.numero || '—'}</td>
                          <td style={{ padding: '13px 16px', fontSize: 14, fontWeight: 500, color: 'var(--ink)', maxWidth: 200 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.titolo || '—'}</div></td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{p.cliente_nome || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)' }}>{p.cantiere_nome || <span style={{ color: 'var(--ds-border)' }}>—</span>}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, fontWeight: 500, color: 'var(--ink)', whiteSpace: 'nowrap' }}>{fmtEur(p.imponibile)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>{fmtData(p.data_emissione)}</td>
                          <td style={{ padding: '13px 16px', fontSize: 11, color: 'var(--ds-muted)', whiteSpace: 'nowrap' }}>{fmtData(p.data_scadenza)}</td>
                          <td style={{ padding: '13px 16px' }}><StatoBadge stato={p.stato} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            )}
          </>
        );
        return isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {listaBody}
            <RankingTable
              title="Top Preventivi per Importo"
              colNome="CLIENTE"
              colValore="IMPORTO"
              colCoda="STATO"
              rows={rankingPreventivi}
              getNome={(row) => row.cliente}
              getSub={(row) => fmtEur(row.totale)}
              renderCoda={renderCodaPreventivi}
            />
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '5fr 2fr', gap: 24, alignItems: 'start' }}>
            <div>{listaBody}</div>
            <RankingTable
              title="Top Preventivi per Importo"
              colNome="CLIENTE"
              colValore="IMPORTO"
              colCoda="STATO"
              rows={rankingPreventivi}
              getNome={(row) => row.cliente}
              getSub={(row) => fmtEur(row.totale)}
              renderCoda={renderCodaPreventivi}
            />
          </div>
        );
      })()}

      {showForm && (
        <Modal title={editing ? 'Modifica preventivo' : 'Nuovo preventivo'} onClose={() => { setShowForm(false); setEditing(null); }}>
          <PreventivoForm initial={editing || {}} clientiList={clientiList} cantieriList={cantieriList} onSave={salva} onCancel={() => { setShowForm(false); setEditing(null); }} saving={saving} />
        </Modal>
      )}
      {dettaglio && (
        <PreventivoDetail preventivo={dettaglio} onClose={() => setDettaglio(null)}
          onEdit={(p) => { setEditing(p); setShowForm(true); setDettaglio(null); }}
          onDelete={elimina} onRefresh={carica} onToast={setToast} />
      )}
      {showImportPdf && (
        <ImportPreventivoPdfModal
          onClose={() => setShowImportPdf(false)}
          onImported={() => { carica(); }}
          clientiList={clientiList}
        />
      )}

      {/* Modal invio multiplo ai clienti */}
      {showInviaClienti && (
        <InvioClientiModal
          documenti={lista.filter((p) => selected.has(p.id))}
          config={PREVENTIVO_INVIO_CONFIG}
          onClose={() => setShowInviaClienti(false)}
          onToast={setToast}
          onDone={() => { setSelected(new Set()); carica(); }}
        />
      )}

      {/* Toast esito invio */}
      {toast && (
        <div onClick={() => setToast(null)} style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, maxWidth: 420, padding: '14px 18px', borderRadius: 'var(--radius-card)', cursor: 'pointer',
          background: toast.type === 'error' ? tint('var(--ds-danger)', 14) : tint('var(--ok)', 14),
          border: `1px solid ${toast.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)'}`,
          color: toast.type === 'error' ? 'var(--ds-danger)' : 'var(--ok)', fontSize: 14, fontWeight: 500, boxShadow: CARD_SHADOW }}>
          {toast.text}
        </div>
      )}
    </div>
  );
}
